import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { getCurrentSpaceId, getCurrentSpaceName } from '@core/selectors/space';
import { getSelectedSpaceId } from '@core/assignments/selectors';
import showToast from '@core/toasts/operations/showToast.operation';
import { accessToken as getAccessToken } from '@core/selectors/user';
import { location } from '@app/selectors/routing';
import launchTurnitinAssignment from '@app/actions/assignments/launchTurnitinAssignment.action';
import launchTurnitinSubmission from '@app/actions/assignments/launchTurnitinSubmission.action';
import { operations, selectors } from '@app/store/assignments';
import { AulaState } from '@core/types/state';
import { getParticipants } from '@core/api/spaces/participants';
import {
  accessingCurrentSpaceAsStaff,
  isInstructor,
} from '@core/selectors/classroom';
import openGroups from '@app/actions/navigation/openGroups.action';
import SubmissionsResponsive from './SubmissionsResponsive';
import { Assignment } from '@core/assignments/types';

const MANAGE_GROUPS = 'manage-groups';
const FOOTER_ACTIONS = 'footer-actions';
const compareSets = (a: { createdAt: string }, b: { createdAt: string }) =>
  (new Date(a?.createdAt) as any) - (new Date(b?.createdAt) as any);

interface SetProps {
  space: string;
  externalId: string;
  groups: string[];
}

const getSetsWithGroups = (sets: any, groups: any, space: string) =>
  Object.values(sets)
    .filter(
      (set: SetProps) =>
        (!space || set?.space === space) &&
        (!('externalId' in set) || !set?.externalId)
    )
    .map((set: SetProps) => ({
      ...set,
      groups: set?.groups?.map((groupId) => groups[groupId]),
    }));

const getSetWithGroupsSelector = () =>
  createSelector(getSetsWithGroups, (setsWithGroups) => setsWithGroups);

const makeSetsWithKeysSelector = createSelector([(sets) => sets], (sets) => {
  const allSets = sets
    .reduce((acc: any, set: any) => {
      if (set?.groups?.length) {
        acc.push(
          {
            isHeader: true,
            key: set?.id,
            label: set?.name,
            studentsWithNoGroup: set?.studentsWithNoGroup,
            externalId: set?.externalId,
          },
          ...[
            // create a flat array of all sets and their groups
            ...set?.groups?.map((group) => ({
              ...group,
              isHeader: false,
              key: group.id,
              label: group.name,
            })),
          ]
        );
      }
      return acc;
    }, [])
    .sort(compareSets);

  // these keys are needed to render the footer items properly by Dropdown component
  allSets.push(
    { label: MANAGE_GROUPS, key: MANAGE_GROUPS, isHeader: false },
    { label: FOOTER_ACTIONS, key: FOOTER_ACTIONS, isHeader: false }
  );
  return allSets;
});

const resolveAssignmentId = (assignment?: {
  id?: string;
  objectId?: string;
  externalId?: string;
}) => assignment?.id || assignment?.objectId || assignment?.externalId || '';

const countSubmitted = (submissions: any[] = []) =>
  submissions?.filter((submission) => Boolean(submission?.id)).length;

// Filters the repeating/empty ids in the array and returns the submissions for the first valid id
// We are passing ids from both URL and current assignment id
// Returns the submissions for the first valid id
const getSubmissionsByIds = (
  getSubmissions: (id: string) => any[],
  paramsAssignmentId?: string,
  currentAssignmentId?: string
) => {
  const uniqueIds = [paramsAssignmentId, currentAssignmentId].filter(
    Boolean
  ) as string[];

  for (const id of uniqueIds) {
    const submissions = getSubmissions(id);
    if (submissions.length) {
      return submissions;
    }
  }
  return [];
};

const makeMapStateToProps = () => {
  const getSetsData = getSetWithGroupsSelector();

  const mapStateToProps = (state: AulaState, ownProps: any) => {
    const spaceId = getCurrentSpaceId(state);
    const { user } = state.user;
    const assignmentId = ownProps.params?.assignmentId;
    const initialExtensionId = location(state)?.query?.extensionId || null;
    const getSubmissions = (id: string) =>
      selectors.getAssignmentSubmissions(state, id);
    const assignment =
      selectors.getSelectedAssignment(state) ??
      selectors.getAssignment(state, assignmentId);
    const { extensions: assignmentExtensions = [] } =
      (assignment as Assignment) || {};
    const hasExtensions = !!assignmentExtensions.length;
    const extensions = hasExtensions
      ? [assignment, ...(assignment?.extensions || [])]
      : [];
    const parentAssignmentSubmissions = getSubmissionsByIds(
      getSubmissions,
      assignmentId,
      resolveAssignmentId(assignment)
    );

    const getSubmissionsFetchedStatus = (id) =>
      selectors.hasFetchedSubmissions(state, id);
    const assignments = selectors.getAssignments(state);

    return {
      sets: makeSetsWithKeysSelector(
        getSetsData(
          state?.data?.groups?.sets,
          state?.data?.groups?.groups,
          spaceId
        )
      ),
      spaceId,
      user,
      assignmentId,
      initialExtensionId,
      getSubmissions,
      assignment,
      extensions: extensions?.map((extension: any) => {
        const extensionSubmissions = getSubmissionsByIds(
          getSubmissions,
          resolveAssignmentId(extension)
        );
        // If the extension has a parentAssignmentId, it is an extension
        const isExtension = Boolean(extension.parentAssignmentId);

        return {
          ...extension,
          studentsCount: isExtension
            ? extension.students?.length || 0
            : parentAssignmentSubmissions.length,
          submissionsCount: isExtension
            ? countSubmitted(extensionSubmissions)
            : countSubmitted(parentAssignmentSubmissions),
        };
      }),
      assignments,
      hasExtensions,
      getSubmissionsFetchedStatus,
      spaceName: getCurrentSpaceName(state),
      selectedSpace: getSelectedSpaceId(state)
        ? state?.data?.classRooms?.[getSelectedSpaceId(state) as string]
        : null,
      accessToken: getAccessToken(state, spaceId),
      parentAssignmentSubmissions,
      hasFetchedAssignments: selectors.hasFetchedAssignments(state),
      isFetchingAssignments: selectors.isFetchingAssignments(state),
      fetchAssignmentsErrored: selectors.isErrored(state),
      fetchSubmissionsErrored: selectors.hasFetchSubmissionsErrored(
        state,
        assignmentId
      ),
      hasFetchedSubmissions: selectors.hasFetchedSubmissions(
        state,
        assignmentId
      ),
      isFetchingSubmissions: selectors.isFetchingSubmissions(
        state,
        assignmentId
      ),
      accessingAsStaff: accessingCurrentSpaceAsStaff(state),
      isEducator: isInstructor(state),
      assignmentUpdateError: selectors.getAssignmentUpdateError(state),
    };
  };

  return mapStateToProps;
};

const mapDispatchToProps = (dispatch) => ({
  deleteSubmission: (params: any) =>
    dispatch(operations.deleteSubmission(params)),
  editExtension: (params: any) => dispatch(operations.editAssignment(params)),
  fetchAssignments: (params: any) =>
    dispatch(operations.fetchAssignments)(params),
  fetchSubmissions: (params: any) =>
    dispatch(operations.fetchSubmissions(params)),
  fetchAllSubmissionsIncludingExtensions: (params: any) =>
    dispatch(operations.fetchAllSubmissionsIncludingExtensions(params)),
  launchTurnitinAssignment,
  launchTurnitinSubmission,
  openGroups: (spaceId) => dispatch(openGroups(spaceId)),
  showToast: (toast) => dispatch(showToast(toast)),
  createExtension: (params: any) =>
    dispatch(operations.createAssignment(params)),
  loadStudents: (spaceId: string) =>
    getParticipants({ spaceId, limit: 2000, role: 'student' }),
  resetExtensionErrors: () => dispatch(operations.resetErrorState()),
});

export default connect(
  makeMapStateToProps,
  mapDispatchToProps
)(SubmissionsResponsive);
